import { Box, Button, Flex, Input, Spinner, Stack, Text } from "@chakra-ui/react";
import moment from "moment";
import { useState } from "react";
import { FaCheckCircle, FaCopy } from "react-icons/fa";

import { AppDatePicker } from "../../components/appDatePicker/appDatePicker";
import { SimpleTooltip } from "../../components/simpleToolTip";
import { Checkbox } from "../../components/ui/checkbox";
import { toaster } from "../../components/ui/toaster";
import { API_KEY_DATE_PAYLOAD } from "../../config/constants";
import { usePostApiKeyMutation } from "../artesian/artesianApi";

import type { ApiKeyCreatePayload, ApiKeyType } from "./apiKeyTypes";
export const ApiKeyForm = () => {
  const [description, setDescription] = useState<string>("");
  const [neverExpires, setNeverExpires] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [saveAction, { data, isLoading }] = usePostApiKeyMutation();
  const [tempData, setTempData] = useState<ApiKeyType | null>(null);
  const isDescriptionValid = !!description;
  const isExpirationValid = neverExpires || !!expirationDate;
  const isFormValid = isDescriptionValid && isExpirationValid;

  const handleSubmit = async () => {
    if (!isFormValid) return;
    const payload = {
      Description: description,
      ExpiresAt: neverExpires ? null : moment(expirationDate).format(API_KEY_DATE_PAYLOAD),
      Id: 0,
      UsagePerDay: -1,
    } as ApiKeyCreatePayload;

    try {
      const result = await saveAction(payload).unwrap();
      setTempData(result);
      toaster.create({
        title: "API Key Created",
        description: "The new API key has been successfully created.",
        type: "success",
        duration: 2000,
      });
    } catch (error) {
      toaster.create({
        title: "Error",
        description: error as string,
        type: "error",
        duration: 2000,
      });
    }
  };
  const isSaveDisabled = () => {
    return !description || (!neverExpires && !expirationDate);
  };
  const handleClear = () => {
    setDescription("");
    setNeverExpires(false);
    setExpirationDate(new Date());
    setTempData(null); // Clear the tempData
  };

  const handleCopy = async () => {
    if (data?.Key) {
      await navigator.clipboard.writeText(data.Key);
      toaster.create({
        title: "Copied",
        description: "The API key has been copied to your clipboard.",
        type: "info",
        duration: 3000,
      });
    }
  };

  return (
    <>
      <Stack gap={4}>
        <Flex direction="column" gap={2}>
          <Text fontWeight="bold">Description</Text>
          <Input
            value={description}
            placeholder="Enter API key description"
            onChange={e => {
              setDescription(e.target.value);
            }}
          />
        </Flex>
        <Text fontWeight="bold" mr={2}>
          Expires
        </Text>
        <Flex alignItems="center">
          <Flex direction="column">
            <Box
              style={{
                opacity: neverExpires ? 0.5 : 1,
                pointerEvents: neverExpires ? "none" : "auto",
                cursor: neverExpires ? "not-allowed" : "pointer",
              }}
            >
              <AppDatePicker
                setDate={setExpirationDate}
                date={expirationDate}
                minDate={new Date()}
                disabled={neverExpires}
              />
            </Box>
          </Flex>
          <Checkbox
            ml={4}
            checked={neverExpires}
            onChange={() => {
              setNeverExpires(!neverExpires);
            }}
          >
            Never Expires
          </Checkbox>
        </Flex>
        <SimpleTooltip
          openDelay={100}
          closeDelay={200}
          content="Please add a description and set and expiration date for the API key."
          disabled={!isSaveDisabled()}
        >
          <span>
            <Button onClick={handleSubmit} w="10%" loading={isLoading} disabled={isSaveDisabled()}>
              Add Key
            </Button>
          </span>
        </SimpleTooltip>
        {isLoading ? (
          <Spinner size="lg" />
        ) : (
          tempData && (
            <Box p={4} borderWidth="1px" borderRadius="md" bg="bg.success" borderColor="border.success">
              <Flex alignItems="center" gap={2}>
                <FaCheckCircle color="green" />
                <Text fontWeight="bold" color="border.success">
                  Please ensure to copy your token now. You will not be able to view it again
                </Text>
              </Flex>
              <Text mt={2} fontSize="sm" fontWeight="bold" color="border.success">
                {tempData.Key}
              </Text>
              <Flex>
                <Button mt={4} mr={2} size="sm" onClick={handleCopy}>
                  <FaCopy />
                  Copy to clipboard
                </Button>
                <Button mt={4} size="sm" colorScheme="dark" onClick={handleClear}>
                  Clear
                </Button>
              </Flex>
            </Box>
          )
        )}
      </Stack>
    </>
  );
};
